import React, { useMemo, useRef } from "react";
import axios from "axios";
import format from "date-fns/format";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import {
  Button,
  Dialog,
  TextInput,
  CheckboxGroup,
  Loader,
  DraftFileUploader,
  SelectInput,
  DateTimePicker,
  Checkbox,
} from "fe-shared/src/components";
import useNonEligibleMaterials from "fe-shared/src/hooks/useNonEligibleMaterials";
import useValidation from "fe-shared/src/hooks/useValidation";
import { getDateString } from "fe-shared/src/helpers/dates";
import { BusinessRole, MaterialCategoryName } from "fe-shared/src/types/domain";

import { Transaction } from "../../types";
import DialogHeader from "../DialogHeader";
import useEstimatedWeight from "../../hooks/useEstimatedWeight";
import { COMMENT_LENGTH_LIMIT } from "../../consts";
import { CreateReceiptFormValues } from "../../types";
import { getValidationConfig, errorMessages } from "./modules/validation";

import styles from "./CreateReceiptDialog.module.scss";

interface StorageResponse {
  storageId: number;
  isVehicle: boolean;
  address: {
    addressLine1?: string;
    city?: string;
    provinceCode?: string;
  };
}

interface StoragesData {
  storages: StorageResponse[];
}

export interface CreateReceiptDialogProps {
  transaction: Transaction;
  isActive: boolean;
  onClose: () => void;
  onSubmit: (values: CreateReceiptFormValues) => void;
  isLoading?: boolean;
}

const defaultMaterialBusinessRoles = [
  BusinessRole.Hauler,
  BusinessRole.Collector,
  BusinessRole.Processor,
  BusinessRole.OneTimePickup,
];

const CreateReceiptDialog = (props: CreateReceiptDialogProps) => {
  const {
    transaction,
    isActive,
    onClose,
    onSubmit,
    isLoading,
  } = props;

  const { t } = useTranslation();

  const maxDateRef = useRef(new Date());

  const targetCompanyId = transaction.targetCompanyInfo?.companyId;

  const {
    data: nonEligibleMaterials,
    isLoading: isNonEligibleLoading,
  } = useNonEligibleMaterials();

  const {
    data: storagesData,
    isLoading: isStoragesLoading,
  } = useQuery<StoragesData>(
    ['storagesForCompany', targetCompanyId],
    () =>
      axios
        .get('storages/getStoragesForCompany', { params: { companyId: targetCompanyId } })
        .then((r) => r.data),
    { enabled: !!targetCompanyId }
  );

  const isTransactionWithTires =
    transaction.materials[0]?.materialCategoryName === MaterialCategoryName.Tires;

  const warehouseOptions = useMemo(
    () =>
      (storagesData?.storages || []).map((s) => ({
        value: s.storageId,
        label: [s.address.addressLine1, s.address.city, s.address.provinceCode]
          .filter(Boolean)
          .join(', '),
      })),
    [storagesData]
  );

  const initialValues = useMemo<CreateReceiptFormValues>(
    () => ({
      scaleTicketNumber: "",
      grossWeight: undefined,
      tareWeight: undefined,
      comment: "",
      scaleTicketFiles: [],
      tirePhotoFiles: [],
      attachments: [],
      targetWarehouseId: null,
      trailerNumber: transaction.trailerNumber ?? null,
      actualTransactionDate: getDateString(new Date()),
      useActualTransactionDateAsScaleTicketDate: true,
    }),
    [transaction.trailerNumber]
  );

  const validationConfig = useMemo(() => getValidationConfig(), []);

  const {
    errors,
    isValid,
    values,
    validateAll,
    updateValue,
    validateValue,
  } = useValidation(validationConfig, initialValues, errorMessages);

  const isCommentTooLong = values.comment.length > COMMENT_LENGTH_LIMIT;
  const characterLimitText = `${values.comment.length.toLocaleString()}/${COMMENT_LENGTH_LIMIT.toLocaleString()}`;

  const updateNumberValue = (key: string, value: string | number) => {
    if (typeof value === "string" && value.trim() === "") {
      updateValue(key, undefined, true);
      return;
    }

    const valueNumber = Number(value);
    const isCorrectValue = !Number.isNaN(valueNumber);
    const savedValue = isCorrectValue ? valueNumber : value;

    updateValue(key, savedValue, true);
  };

  const materialVariants = useMemo(
    () =>
      transaction.materials.map((m) => ({
        id: m.materialCode,
        title: m.materialName,
        value: m.materialCode,
      })),
    [transaction.materials]
  );

  const materialCodes = useMemo(
    () => transaction.materials.map((m) => m.materialCode),
    [transaction.materials]
  );

  const stuffVariants = useMemo(
    () =>
      (nonEligibleMaterials || []).map((item) => ({
        id: item.code,
        title: item.name,
        value: item.code,
      })),
    [nonEligibleMaterials]
  );

  const estimatedWeight = useEstimatedWeight(
    transaction.materials,
    defaultMaterialBusinessRoles
  );

  const netWeight =
    values.grossWeight !== undefined && values.tareWeight !== undefined
      ? (values.grossWeight - values.tareWeight).toLocaleString()
      : "0";

  // Validation errors are created only for dirty fields, so keep submit disabled before the date field is touched.
  const isScaleTicketDateMissing =
    values.useActualTransactionDateAsScaleTicketDate &&
    !values.actualTransactionDate;
  const scaleTicketDateDisplay = values.actualTransactionDate
    ? format(new Date(values.actualTransactionDate), "dd/MM/yy")
    : "—";

  // Untouched required fields have no visible errors but must still block submission.
  const isSubmitDisabled =
    !isValid ||
    !!Object.keys(errors).length ||
    isScaleTicketDateMissing ||
    isCommentTooLong ||
    !values.scaleTicketFiles.length ||
    !values.scaleTicketNumber ||
    !values.targetWarehouseId;

  const handleSubmit = () => {
    if (!isSubmitDisabled) {
      onSubmit(values);
    } else {
      validateAll();
    }
  };

  const actions = [
    <Button
      key="1"
      id="create-receipt-dialog-submit-btn"
      disabled={isSubmitDisabled}
      variant="filled"
      onClick={handleSubmit}
    >
      {t("createReceiptDialog.buttons.createReceipt")}
    </Button>,
    <Button
      key="2"
      id="create-receipt-dialog-cancel-btn"
      variant="outlined"
      onClick={onClose}
    >
      {t("cancel")}
    </Button>,
  ];

  const title = (
    <DialogHeader
      type={transaction.transactionType}
      title={t("createReceiptDialog.title")}
      translationRoot="transactions"
      number={transaction.transactionNumber}
    />
  );

  return (
    <Dialog
      modal
      width={608}
      title={title}
      isActive={isActive}
      onClose={onClose}
      actions={actions}
    >
      {(isLoading || isNonEligibleLoading || isStoragesLoading) && (
        <Loader isBlocking />
      )}

      <span className={styles.formSectionTitle}>
        {t("createReceiptDialog.materialsTitle")}
      </span>
      <CheckboxGroup
        id="materials"
        value={materialCodes}
        onChange={() => { }}
        disabled
        name="create-receipt-materials-checkbox-group"
        variants={materialVariants}
        wrapperClassName={styles.checkboxWrapper}
      />
      <span className={styles.formSectionTitle}>
        {t("createReceiptDialog.quantityTitle")}
      </span>
      {transaction.materials.map((m) => (
        <div key={m.materialCode} className={styles.tiresCount}>
          <div className={styles.tiresCountTitle}>{m.materialName}</div>
          <TextInput
            id={`material-units-${m.materialCode}`}
            value={m.units.unitQuantity}
            onChange={() => { }}
            disabled
            wrapperClassName={styles.tiresCountInput}
          />
        </div>
      ))}

      {isTransactionWithTires && (
        <>
          <span className={styles.formSectionSubtitle}>
            {t("correction.dialog.form.otherStuff")}
          </span>
          <CheckboxGroup
            inline
            id="materialAttributes"
            value={transaction.materialAttributes}
            onChange={() => { }}
            disabled
            name="create-receipt-stuff-checkbox-group"
            variants={stuffVariants}
            wrapperClassName={styles.checkboxWrapper}
          />
        </>
      )}
      {transaction.trailerNumber && (
        <>
          <span className={styles.formSectionTitle}>
            {t("transactions.details.trailerNumber.label")}
          </span>
          <TextInput
            id="trailerNumber"
            value={values.trailerNumber ?? ""}
            onChange={() => { }}
            disabled
          />
        </>
      )}
      <>
        <span className={styles.formSectionTitle}>
          {t("createReceiptDialog.whereToMoveLoad")}
          <span className={styles.titleRequired}>*</span>
        </span>
        <SelectInput
          id="targetWarehouseId"
          placeholder={t("createReceiptDialog.whereToMoveLoadPlaceholder")}
          options={warehouseOptions}
          value={values.targetWarehouseId ?? ''}
          disabled={isStoragesLoading}
          isError={!!errors.targetWarehouseId}
          errorText={errors.targetWarehouseId}
          onBlur={() => validateValue("targetWarehouseId")}
          onChange={(event, value) => {
            event.preventDefault();
            updateValue(
              'targetWarehouseId',
              value !== null && value !== undefined ? Number(value) : null,
              true
            );
          }}
        />
      </>
      <>
        <span className={styles.formSectionTitle}>
          {t("createReceiptDialog.transactionDate")}
        </span>
        <DateTimePicker
          id="actualTransactionDate"
          maxDate={maxDateRef.current}
          placeholder={t("createReceiptDialog.transactionDate")}
          value={values.actualTransactionDate}
          isError={!!errors.actualTransactionDate}
          errorText={errors.actualTransactionDate}
          onChange={(value) =>
            updateValue("actualTransactionDate", value, true)
          }
        />
        <div className={styles.scaleTicketDateOption}>
          <Checkbox
            id="useActualTransactionDateAsScaleTicketDate"
            value={values.useActualTransactionDateAsScaleTicketDate}
            title={t(
              "createReceiptDialog.useTransactionDateAsScaleTicketDate"
            )}
            onChange={(_event, value) => {
              updateValue(
                "useActualTransactionDateAsScaleTicketDate",
                value,
                true
              );
              if (value && !values.actualTransactionDate) {
                validateValue("actualTransactionDate");
              }
            }}
          />
          <span className={styles.scaleTicketDateHelp}>
            {t("createReceiptDialog.scaleTicketDateHelp", {
              date: scaleTicketDateDisplay,
            })}
          </span>
        </div>
      </>
      <>
        <span className={styles.formSectionTitle}>
          {t("createReceiptDialog.scaleTicketNumber")}
          <span className={styles.titleRequired}>*</span>
        </span>
        <TextInput
          id="scaleTicketNumber"
          value={values.scaleTicketNumber}
          isError={!!errors.scaleTicketNumber}
          errorText={errors.scaleTicketNumber}
          onBlur={() => validateValue("scaleTicketNumber")}
          onChange={(e, value) => updateValue("scaleTicketNumber", value, true)}
        />
      </>
      <div className={styles.weightSection}>
        <span>{t("transactions.details.weights.netWeight")}</span>
        <span>{`${netWeight} kg`}</span>
      </div>
      <div className={styles.estimatedWeightSection}>
        <span>{t("transactions.details.weights.estimatedWeight")}</span>
        <span>{`${estimatedWeight?.toLocaleString()} kg`}</span>
      </div>

      <div className={styles.weightCount}>
        <label htmlFor="grossWeight" className={styles.weightCountTitle}>
          {t("transactions.details.weights.grossWeight")}
          <span className={styles.titleRequired}>*</span>
        </label>
        <TextInput
          id="grossWeight"
          mask="99999999999999"
          errorText={errors.grossWeight}
          isError={!!errors.grossWeight}
          value={values.grossWeight ?? ""}
          width={102}
          wrapperClassName={styles.weightCountInput}
          onBlur={() => validateValue("grossWeight")}
          onChange={(e, value) => updateNumberValue("grossWeight", value)}
        />
      </div>
      <div className={styles.weightCount}>
        <label htmlFor="tareWeight" className={styles.weightCountTitle}>
          {t("transactions.details.weights.tareWeight")}
          <span className={styles.titleRequired}>*</span>
        </label>
        <TextInput
          id="tareWeight"
          mask="99999999999999"
          errorText={errors.tareWeight}
          isError={!!errors.tareWeight}
          value={values.tareWeight ?? ""}
          width={102}
          wrapperClassName={styles.weightCountInput}
          onBlur={() => validateValue("tareWeight")}
          onChange={(e, value) => updateNumberValue("tareWeight", value)}
        />
      </div>

      <TextInput
        isMultiline
        id="comment"
        title={t("correction.dialog.inputs.comment")}
        errorText={t("validation.commentMaxLength", { characterLimitText })}
        isError={isCommentTooLong}
        value={values.comment}
        onBlur={() => validateValue("comment")}
        onChange={(e, value) => updateValue("comment", value, true)}
      />

      <span className={styles.formSectionTitle}>
        {t("createReceiptDialog.uploadScaleTickets")}
        <span className={styles.titleRequired}>*</span>
      </span>
      <DraftFileUploader
        onChange={(files) => updateValue("scaleTicketFiles", files, true)}
        path={`transactions/scaleTicket?transactionId=${transaction.transactionId}`}
        uploadKey="file"
        files={values.scaleTicketFiles}
        externalHintText="createReceiptDialog.imageUploadHint"
      />

      <span className={styles.formSectionTitle}>
        {t("createReceiptDialog.uploadTirePhotos")}
      </span>
      <DraftFileUploader
        onChange={(files) => updateValue("tirePhotoFiles", files, true)}
        path={`transactions/tirePhoto?transactionId=${transaction.transactionId}`}
        uploadKey="file"
        files={values.tirePhotoFiles}
        externalHintText="createReceiptDialog.imageUploadHint"
      />

      <span className={styles.formSectionTitle}>
        {t("createReceiptDialog.uploadSupportingDocs")}
      </span>
      <DraftFileUploader
        onChange={(files) => updateValue("attachments", files, true)}
        path="transactions/attachment"
        files={values.attachments}
        externalHintText="createReceiptDialog.supportingDocumentsUploadHint"
      />
    </Dialog>
  );
};

export default CreateReceiptDialog;
